import type { Category, Product } from '@scayle/storefront-nuxt'
import { useSearch } from '@scayle/storefront-search/composables'
import { useDebounceFn } from '@vueuse/core'

import {
  DEBOUNCED_SEARCH_DURATION,
  MIN_CHARS_FOR_SEARCH,
  PRODUCT_LIMIT,
  SEARCH_WITH_PARAMS,
} from '~/constants/search'

const useSearchResults = () => {
  const {
    searchQuery,
    status: searchStatus,
    products: productSuggestions,
    categories: categorySuggestions,
    resetSearch,
    getSearchSuggestions,
  } = useSearch()

  const productParams = ref()
  const {
    data,
    status: productsStatus,
    refresh: fetchProducts,
  } = useProducts(
    {
      options: { immediate: false, lazy: false },
      params: productParams,
    },
    'useSearchResults',
  )

  const isStarted = ref(false)
  const categories = ref<Category[]>([])
  const products = ref<Product[]>([])
  let requestCount = 0

  const searchFor = async (term: string) => {
    productParams.value = {
      path: '/',
      perPage: PRODUCT_LIMIT,
      where: {
        term,
      },
      with: SEARCH_WITH_PARAMS?.products,
    }
    const currentRequest = ++requestCount

    await Promise.all([getSearchSuggestions(), fetchProducts()])

    if (currentRequest !== requestCount) {
      return // Ignore outdated requests
    }

    categories.value =
      categorySuggestions.value
        ?.map((suggestion) => suggestion.categorySuggestion.category)
        .slice(0, 3) || []

    const exactProductSuggestion =
      productSuggestions.value?.[0]?.productSuggestion.product

    if (exactProductSuggestion) {
      products.value = [exactProductSuggestion]
    } else {
      products.value = data.value?.products || []
    }

    isStarted.value = false
  }

  const debouncedSearch = useDebounceFn(searchFor, DEBOUNCED_SEARCH_DURATION)

  watch(searchQuery, () => {
    const { value: query = '' } = searchQuery

    if (!searchQuery.value) {
      isStarted.value = false
      searchQuery.value = ''
      categories.value = []
      products.value = []
      resetSearch()
    } else if (searchQuery.value.length < MIN_CHARS_FOR_SEARCH) {
      isStarted.value = false
    } else {
      isStarted.value = true
      debouncedSearch(query)
    }
  })

  const hasSearchQuery = computed(
    () => searchQuery.value.length >= MIN_CHARS_FOR_SEARCH,
  )

  const count = computed(() => products.value.length + categories.value.length)

  return {
    count,
    hasSearchQuery,
    products,
    categories,
    fetching: computed(
      () =>
        isStarted.value ||
        productsStatus.value === 'pending' ||
        searchStatus.value === 'pending',
    ),
    resetSearch,
    searchQuery,
  }
}

let singleton: ReturnType<typeof useSearchResults>

export default () => {
  if (singleton && import.meta.client) {
    return singleton
  }
  singleton = useSearchResults()
  return singleton
}
